import { useEffect, useState } from "react";
import { api, fmtNumber } from "@/lib/api";
import { SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { TrendingUp, TrendingDown, Pencil, X, Archive, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const REC_STYLE = {
  Keep: "bg-emerald-50 border-emerald-200 text-emerald-800",
  Monitor: "bg-amber-50 border-amber-200 text-amber-800",
  Replace: "bg-rose-50 border-rose-200 text-rose-800",
};
const SVC_BADGE = {
  overdue: ["Overdue", "bg-rose-50 text-rose-700 border-rose-200"],
  due_soon: ["Due soon", "bg-amber-50 text-amber-700 border-amber-200"],
  ok: ["OK", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  unknown: ["Unknown", "bg-slate-100 text-slate-500 border-slate-200"],
};

const RATING_FIELDS = ["condition", "utilisation", "environment", "risk", "end_of_life"];

const Info = ({ label, value }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="mt-1 text-sm text-slate-900">{value ?? "—"}</div>
  </div>
);

const Field = ({ label, ...props }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    <Input className="h-8 text-sm" {...props} />
  </div>
);

// Turn an AroFlo push result into a user-facing toast.
const arofloToast = (results) => {
  const merged = results.filter(Boolean);
  if (!merged.length) return;
  const unmatched = [...new Set(merged.flatMap((r) => r.unmatched_fields || []))];
  if (merged.some((r) => r.status === "failed")) {
    toast.warning("Saved in hub — AroFlo push failed (logged, will retry)");
  } else if (unmatched.length) {
    toast.success(`Saved in hub · AroFlo has no field for: ${unmatched.join(", ")}`);
  } else if (merged.some((r) => r.status === "ok")) {
    toast.success("Saved · pushed to AroFlo");
  } else {
    const reason = merged.find((r) => r.reason)?.reason;
    toast.success(`Saved in hub${reason ? ` · AroFlo: ${reason}` : ""}`);
  }
};

export const VehicleScorecard = ({ rego, canEdit = false, onSaved }) => {
  const [d, setD] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [orig, setOrig] = useState({});
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const load = () => api.get(`/fleet/vehicles/${rego}/scorecard`).then((r) => setD(r.data));

  const toggleArchive = async () => {
    const next = d?.vehicle?.status !== "ARCHIVED";
    if (next && !window.confirm(`Archive ${rego}? It will drop off the active Fleet list (records are kept).`)) return;
    setArchiving(true);
    try {
      await api.put(`/vehicles/${rego}/archive-status`, { archived: next });
      toast.success(next ? `${rego} archived` : `${rego} restored`);
      await load();
      onSaved && onSaved();
    } catch (e) {
      toast.error("Couldn't update archive status");
    } finally {
      setArchiving(false);
    }
  };

  useEffect(() => {
    setD(null);
    setEditing(false);
    load();
  }, [rego]);

  const startEdit = () => {
    const v = d.vehicle, r = d.ratings;
    const snap = {
      driver: v.driver && v.driver !== "—" ? v.driver : "",
      last_service_date: v.last_service_date || "",
      last_service_km: v.last_service_km ?? "",
      odometer_km: v.odometer_km ?? "",
      next_service_date: v.next_service_date || "",
      next_service_km: v.next_service_km ?? "",
      rego_expiry: v.rego_expiry || "",
      condition: r.condition ?? "",
      utilisation: r.utilisation ?? "",
      environment: r.environment ?? "",
      risk: r.risk ?? "",
      end_of_life: r.end_of_life ?? "",
      eol_suggestion: r.eol_suggestion ?? "",
    };
    setForm(snap);
    setOrig(snap);
    setEditing(true);
  };

  const set = (k, val) => setForm((f) => ({ ...f, [k]: val }));
  const chg = (k) => String(form[k] ?? "") !== String(orig[k] ?? "");
  const num = (x) => {
    if (x === "" || x == null) return undefined;
    const n = Number(x);
    return Number.isFinite(n) ? n : undefined;
  };

  const save = async () => {
    setSaving(true);
    const results = [];
    let hadError = false;
    let didSomething = false;
    try {
      if (chg("driver")) {
        try {
          const res = await api.put(`/vehicles/${rego}/driver`, { driver: form.driver.trim() });
          didSomething = true;
          if (res.data.aroflo) results.push(res.data.aroflo);
          else toast.success(res.data.cleared ? "Driver override cleared — using AroFlo value" : "Driver saved in hub");
        } catch (e) { hadError = true; toast.error(`Driver: ${e.response?.data?.detail || "save failed"}`); }
      }

      const svcBody = {};
      if (chg("last_service_date") && form.last_service_date) svcBody.last_service_date = form.last_service_date;
      if (chg("next_service_date") && form.next_service_date) svcBody.next_service_date = form.next_service_date;
      if (chg("rego_expiry")) svcBody.rego_expiry = form.rego_expiry || "";
      ["last_service_km", "odometer_km", "next_service_km"].forEach((f) => {
        if (chg(f) && num(form[f]) !== undefined) svcBody[f] = num(form[f]);
      });
      if (Object.keys(svcBody).length) {
        try {
          const res = await api.put(`/vehicles/${rego}/service`, svcBody);
          results.push(res.data.aroflo);
        } catch (e) { hadError = true; toast.error(`Service: ${e.response?.data?.detail || "save failed"}`); }
      }

      const ratBody = {};
      RATING_FIELDS.forEach((f) => { if (chg(f)) ratBody[f] = form[f] === "" ? null : form[f]; });
      if (chg("eol_suggestion")) ratBody.eol_suggestion = form.eol_suggestion === "" ? null : form.eol_suggestion;
      if (Object.keys(ratBody).length) {
        try {
          const res = await api.put(`/vehicles/${rego}/asset-fields`, ratBody);
          results.push(res.data.aroflo);
        } catch (e) { hadError = true; toast.error(`Ratings: ${e.response?.data?.detail || "save failed"}`); }
      }

      if (results.length) arofloToast(results);
      else if (!hadError && !didSomething) toast.info("No changes to save");

      if (!hadError) setEditing(false);
      await load();
      onSaved && onSaved();
    } finally {
      setSaving(false);
    }
  };

  if (!d) return (
    <>
      <SheetHeader className="sr-only"><SheetTitle>Vehicle scorecard</SheetTitle></SheetHeader>
      <div className="mt-6 h-64 shimmer rounded-lg" />
    </>
  );
  const { vehicle: v, service: s, maintenance: m, ratings, recommendation: rec } = d;
  const [svcLabel, svcCls] = SVC_BADGE[s.service_status] || SVC_BADGE.unknown;

  return (
    <div data-testid="vehicle-scorecard">
      <SheetHeader>
        <SheetTitle className="font-display flex items-center gap-3">
          <span className="font-mono text-gbe">{v.rego}</span>
          <span className="text-slate-400 text-sm font-normal">{v.make} {v.model}</span>
          {canEdit && !editing && (
            <span className="ml-auto inline-flex items-center gap-2">
              <button data-testid="scorecard-archive-btn" onClick={toggleArchive} disabled={archiving}
                className={`inline-flex items-center gap-1.5 text-xs border rounded-md px-2.5 py-1 disabled:opacity-50 ${v.status === "ARCHIVED" ? "text-emerald-700 border-emerald-200 hover:bg-emerald-50" : "text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                {v.status === "ARCHIVED" ? <><RotateCcw className="w-3.5 h-3.5" /> Restore</> : <><Archive className="w-3.5 h-3.5" /> Archive</>}
              </button>
              <button data-testid="scorecard-edit-btn" onClick={startEdit}
                className="inline-flex items-center gap-1.5 text-xs text-gbe border border-slate-200 rounded-md px-2.5 py-1 hover:bg-slate-50">
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
            </span>
          )}
        </SheetTitle>
        <SheetDescription>{v.type} · Driver: {v.driver || "—"}</SheetDescription>
      </SheetHeader>

      {editing ? (
        <div data-testid="scorecard-edit-panel" className="mt-4 rounded-lg border border-gbe/30 bg-slate-50/60 p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-slate-900">Update service &amp; ratings</h4>
            <button data-testid="scorecard-edit-cancel" onClick={() => setEditing(false)} className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mb-2">Driver</div>
          <div className="mb-4">
            <Field label="Assigned driver" data-testid="edit-driver"
              value={form.driver} onChange={(e) => set("driver", e.target.value)}
              placeholder="e.g. Peter Chivers" />
            <div className="text-[11px] text-slate-400 mt-1">Managed in the Hub — survives the nightly AroFlo sync. Clear the field to revert to the AroFlo value.</div>
          </div>

          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mb-2">Service</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Last service date" type="date" data-testid="edit-last-service-date"
              value={form.last_service_date} onChange={(e) => set("last_service_date", e.target.value)} />
            <Field label="Odometer (km)" type="number" data-testid="edit-odometer-km"
              value={form.odometer_km} onChange={(e) => set("odometer_km", e.target.value)} placeholder="e.g. 84200" />
            <Field label="Last service km" type="number" data-testid="edit-last-service-km"
              value={form.last_service_km} onChange={(e) => set("last_service_km", e.target.value)} placeholder="km at last service" />
            <Field label="Next service date" type="date" data-testid="edit-next-service-date"
              value={form.next_service_date} onChange={(e) => set("next_service_date", e.target.value)} />
            <Field label="Next service km" type="number" data-testid="edit-next-service-km"
              value={form.next_service_km} onChange={(e) => set("next_service_km", e.target.value)} placeholder="override due km" />
            <Field label="Registration expiry" type="date" data-testid="edit-rego-expiry"
              value={form.rego_expiry} onChange={(e) => set("rego_expiry", e.target.value)} />
          </div>

          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mt-4 mb-2">Ratings <span className="lowercase font-normal text-slate-400">(1–5 or Good/Poor — pushed to AroFlo as 1–5)</span></div>
          <div className="grid grid-cols-3 gap-3">
            {RATING_FIELDS.map((k) => (
              <Field key={k} label={k === "end_of_life" ? "Life Exp (yrs)" : k.replace(/_/g, " ")} data-testid={`edit-rating-${k}`}
                value={form[k]} onChange={(e) => set(k, e.target.value)}
                placeholder={k === "end_of_life" ? "years left e.g. 2" : "1–5 / Good…"} />
            ))}
          </div>
          <div className="mt-3">
            <Field label="End-of-life note" data-testid="edit-eol-suggestion"
              value={form.eol_suggestion} onChange={(e) => set("eol_suggestion", e.target.value)}
              placeholder="e.g. Replace FY28 — high kms, gearbox issues" />
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-xs border border-slate-200 rounded-md bg-white">Cancel</button>
            <button data-testid="scorecard-save-btn" disabled={saving} onClick={save}
              className="px-3 py-1.5 text-xs bg-[#00334C] text-white rounded-md disabled:opacity-60">
              {saving ? "Saving…" : "Save & push to AroFlo"}
            </button>
          </div>
        </div>
      ) : (
        <div data-testid="scorecard-recommendation" className={`mt-4 rounded-lg border px-4 py-3 ${REC_STYLE[rec.label]}`}>
          <div className="text-sm font-semibold">{rec.label} — replacement score {d.replacement_score}/100 <span className="font-normal opacity-70">({d.signals_available} signals)</span></div>
          <ul className="mt-1.5 space-y-0.5">
            {rec.reasons.map((r, i) => (<li key={i} className="text-xs opacity-90">• {r}</li>))}
          </ul>
        </div>
      )}

      <div className="mt-5 grid grid-cols-2 gap-4">
        <Info label="Service status" value={<span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-medium ${svcCls}`}>{svcLabel}</span>} />
        <Info label="Next due" value={s.next_due_date
          ? <span className={s.service_status === "overdue" ? "text-rose-600 font-medium" : undefined}>
              {s.next_due_date}{s.days_to_due != null ? (s.days_to_due < 0 ? ` (${-s.days_to_due}d overdue)` : ` (in ${s.days_to_due}d)`) : ""}
            </span>
          : "—"} />
        <Info label="Last service" value={v.last_service_date
          ? <span>{v.last_service_date}{s.days_since_service != null ? ` (${s.days_since_service}d ago)` : ""}
              {v.last_service_source === "job" && (
                <span className="ml-1 text-[10px] text-emerald-600 font-medium" data-testid="last-service-from-job">· from completed job{v.last_service_job ? ` JN ${v.last_service_job}` : ""}</span>
              )}
            </span>
          : "—"} />
        <Info label="Odometer" value={v.odometer_km
          ? <span>{fmtNumber(v.odometer_km)} km
              {v.odometer_source && <span className="text-[11px] text-slate-400"> · {v.odometer_source}{v.odometer_as_of ? ` @ ${v.odometer_as_of}` : ""}</span>}
            </span>
          : "—"} />
        <Info label="Interval" value={`${s.interval_months} months / ${fmtNumber(s.interval_km)} km`} />
        <Info label="Base" value={v.base} />
      </div>

      <div className="mt-6">
        <h4 className="text-sm font-medium text-slate-900 mb-2">Condition ratings</h4>
        <div className="grid grid-cols-3 gap-2">
          {["condition", "utilisation", "environment", "risk", "end_of_life"].map((k) => (
            <div key={k} className="border border-slate-200 rounded-md px-2.5 py-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">{k === "end_of_life" ? "Life Exp (yrs)" : k.replace(/_/g, " ")}</div>
              <div className="text-sm text-slate-800 mt-0.5">{ratings[k] ?? "—"}</div>
            </div>
          ))}
        </div>
        {ratings.eol_suggestion && <div className="mt-2 text-xs text-amber-700 italic">"{ratings.eol_suggestion}"</div>}
        <div className="text-[11px] text-slate-400 mt-1.5">
          {canEdit ? "Use Edit above to update ratings — changes push to AroFlo." : "Ratings drive the replacement score."}
        </div>
      </div>

      <div className="mt-6" data-testid="scorecard-maintenance">
        <h4 className="text-sm font-medium text-slate-900 mb-2">Maintenance spend</h4>
        <div className="grid grid-cols-3 gap-2">
          <div className="border border-slate-200 rounded-md px-2.5 py-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Last 12 mo</div>
            <div className="text-sm font-semibold text-slate-900 tabular-nums">${fmtNumber(m.cost_12mo)}</div>
          </div>
          <div className="border border-slate-200 rounded-md px-2.5 py-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Prior 12 mo</div>
            <div className="text-sm text-slate-700 tabular-nums">${fmtNumber(m.cost_prior_12mo)}</div>
          </div>
          <div className="border border-slate-200 rounded-md px-2.5 py-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Trend</div>
            <div className={`text-sm font-medium tabular-nums flex items-center gap-1 ${m.trend_pct == null ? "text-slate-400" : m.trend_pct > 0 ? "text-rose-600" : "text-emerald-600"}`}>
              {m.trend_pct == null ? "—" : (<>{m.trend_pct > 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}{m.trend_pct > 0 ? "+" : ""}{m.trend_pct}%</>)}
            </div>
          </div>
        </div>
        <div className="text-[11px] text-slate-400 mt-1.5">{m.note}</div>
        <h4 className="text-sm font-medium text-slate-900 mt-4 mb-2">Service &amp; repair history {m.jobs.length > 0 && <span className="text-slate-400 font-normal">({m.jobs.length})</span>}</h4>
        {m.jobs.length === 0 ? (
          <div className="text-xs text-slate-400">No AroFlo vehicle service jobs found for this vehicle's driver.</div>
        ) : (
          <ul className="space-y-2">
            {m.jobs.map((j, i) => (
              <li key={i} className="text-sm border-l-2 border-gbe/30 pl-3 py-1 flex items-start justify-between gap-3">
                <div>
                  <div className="text-slate-800">{j.description || "Vehicle service"}</div>
                  <div className="text-[11px] text-slate-500">{j.date || "—"}{j.job_number ? ` · JN ${j.job_number}` : ""} · {j.status || ""}</div>
                </div>
                <div className="text-sm tabular-nums text-slate-700 whitespace-nowrap" title={j.cost > 0 ? "" : "Cost appears once the job is invoiced/closed in AroFlo"}>
                  {j.cost > 0 ? `$${fmtNumber(j.cost)}` : <span className="text-slate-400 text-xs italic">not costed yet</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
